import { router } from "@inertiajs/react";
import { Button, Divider, Group, Modal, TextInput } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const generateAdvName = (baseName) => {
    let copyCount = 1;
    const regex = /\(copy(?: (\d+))?\)/g; // Match (copy) and (copy X)

    let match;
    let highestCopy = 0;
    while ((match = regex.exec(baseName)) !== null) {
        if (match[1]) {
            highestCopy = Math.max(highestCopy, parseInt(match[1], 10));
        } else {
            highestCopy++;
        }
    }

    copyCount = highestCopy + 1;

    let cleanedName = baseName.replace(regex, "").trim();

    if (copyCount == 1) {
        return `${cleanedName} (copy)`;
    }

    return `${cleanedName} (copy ${copyCount})`;
};

export const useDuplicateAdModal = (ad) => {
    const { t } = useTranslation();
    const [opened, { open: openModal, close }] = useDisclosure(false);
    const [advName, setAdvName] = useState(ad?.name || '');

    const open = () => {
        setAdvName(generateAdvName(ad?.name));
        openModal();
    };

    const submit = async () => {

        const id = notifications.show({
            loading: true,
            title: t('ads.duplicating'),
            message: t('ads.duplicatingMessage'),
            autoClose: false,
            withCloseButton: false,
        });

        try {
            // AD-261: a new-editor ad takes the name with the copy (its campaign name); any
            // other ad is renamed by a second request.
            const isEditorV2 = Number(ad?.ad_origin) === 9;
            const { data } = await axios.post(route('studio.v1.api.ads.duplicate', ad?.id), {
                name: advName || '',
            });

            const newAdId = data?.ad?.id;

            if (!newAdId) {
                throw new Error('Failed to duplicate ad.');
            }

            if (!isEditorV2) {
                await axios.put(route('studio.v1.api.ads.update', newAdId), {
                    name: advName || '',
                });
            }

            router.visit(isEditorV2
                ? route('studio.studio_editor_v2.edit', { adId: newAdId })
                : route('studio.ad_builder.edit', { ad: newAdId }), {
                preserveState: true,
                replace: true
            });

            notifications.update({
                id,
                color: 'green',
                title: t('ads.duplicateSuccess'),
                message: t('ads.duplicateSuccessMessage'),
                icon: <IconCheck size={18} />,
                loading: false,
                autoClose: 4000,
            });
        } catch (error) {

            notifications.update({
                id,
                color: 'red',
                title: t('ads.duplicateError'),
                // The server's refusal when it has words for the user (a locked creative, a
                // design the editor cannot open), else the generic message.
                message: error?.response?.data?.message || t('ads.duplicateErrorMessage'),
                icon: <IconX size={18} />,
                loading: false,
                autoClose: 4000,
            });

            console.error('An error occurred:', error);
        }
    };

    const modal = (
        <Modal opened={opened} onClose={close} title={t('ads.duplicateModalTitle')} radius="lg">
            <Divider mb="md" />
            <TextInput
                variant="filled"
                radius="md"
                value={advName}
                label={t('adBuilder.advertiserName')}
                onChange={(event) => setAdvName(event.currentTarget.value)}
                placeholder={t('adBuilder.advertiserNamePlaceholder')}
            />
            <Divider my="md" />

            <Group justify="flex-end">
                <Button variant="outline" color="gray" radius="md" size="xs" onClick={close}>{t('common.cancel')}</Button>
                <Button variant="filled" color="smartico-blue" radius="md" size="xs" onClick={submit} leftSection={<IconCopy size={16} />}>{t('ads.duplicateAd')}</Button>
            </Group>
        </Modal>
    );

    return { open, modal };
};
